import React from 'react';
import { brand, forAudience, Project, audiences } from '../../config/brand';
import { useReveal } from '../../hooks/useReveal';
import { useAudience } from '../Audience/AudienceContext';
import AudienceSwitch from '../Audience/AudienceSwitch';
import SplitText from '../SplitText';
import './Projects.css';

const statusLabel: Record<string, { label: string; className: string }> = {
  live:   { label: 'Live',         className: 'projects__status--live' },
  sunset: { label: 'Sunset',       className: 'projects__status--sunset' },
  fun:    { label: 'Side project', className: 'projects__status--fun' },
};

const Projects: React.FC = () => {
  const ref = useReveal();
  const { audience } = useAudience();
  const projects = forAudience(brand.projects as Project[], audience);
  const blurb = audiences.find((a) => a.id === audience)?.blurb;

  return (
    <section className="projects section" id="projects" aria-labelledby="projects-heading" ref={ref}>
      <div className="container">
        <div className="section-head">
          <div>
            <span className="section-label">Selected Work</span>
            <h2 className="section-title reveal" id="projects-heading">
              <SplitText text="Products we've shipped" highlight="shipped" />
            </h2>
            <p className="section-subtitle reveal reveal-delay-1">
              Real products — some live, some retired, all built and shipped by us.
            </p>
          </div>

          {/* Track filter (P3): an enterprise buyer never lands on consumer work */}
          <div className="projects__filter reveal reveal-delay-1">
            <AudienceSwitch id="work-track" label="Filter work by track" />
            {blurb && audience !== 'all' && (
              <span className="audience-blurb"><span key={audience}>{blurb}</span></span>
            )}
          </div>
        </div>

        <div className="projects__grid">
          {projects.map((project, i) => {
            const st = project.status ? statusLabel[project.status] : null;
            const featured = project.status === 'live';
            const isDarkLogo = !!project.logo && /\.(jpe?g)$/i.test(project.logo);
            return (
              <article
                className={`projects__card card reveal reveal-delay-${Math.min(i + 1, 6)}${featured ? ' projects__card--featured' : ''}`}
                key={project.title}
              >
                <div className="projects__card-top">
                  <div className={`projects__card-logo-bg${isDarkLogo ? ' projects__card-logo-bg--dark' : ''}`}>
                    {project.logo ? (
                      <img src={project.logo} alt={`${project.title} logo`} className="projects__card-logo" />
                    ) : (
                      <span className="projects__card-logo-placeholder" aria-hidden="true">◈</span>
                    )}
                  </div>
                  <div className="projects__card-top-right">
                    {st && (
                      <span className={`projects__card-status ${st.className}`}>
                        <i className="projects__status-dot" aria-hidden="true" />
                        {st.label}
                      </span>
                    )}
                    {project.link && (
                      <a
                        href={project.link}
                        className="projects__card-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${project.title}`}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7M8 7h9v9"/></svg>
                      </a>
                    )}
                  </div>
                </div>

                <div className="projects__card-head">
                  <h3 className="projects__card-title">{project.title}</h3>
                  <span className="projects__card-category">{project.category}</span>
                </div>
                <p className="projects__card-desc">{project.description}</p>

                <div className="projects__card-footer">
                  <div className="projects__card-tags">
                    {project.tags.slice(0, 4).map((tag) => (
                      <span className="tag" key={tag}>{tag}</span>
                    ))}
                  </div>
                  {project.metric && (
                    <span className="projects__card-metric">{project.metric}</span>
                  )}
                </div>
              </article>
            );
          })}

          {projects.length === 0 && (
            <p className="audience-empty">Nothing under this track yet — try “Everything”.</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Projects;
